import type { ReactNode } from 'react';
import { BookOpen, ShieldCheck, Stethoscope } from 'lucide-react';
import { BRAND } from '@medicail/shared';
import { BrandMark, Wordmark } from '@/components/ui';

const PROMISES = [
  { icon: BookOpen, text: 'Grounded in worldwide clinical guidelines' },
  { icon: Stethoscope, text: 'Pointers to top doctors near you' },
  { icon: ShieldCheck, text: 'Private by design, and honest about its limits' },
];

export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-dvh bg-canvas lg:grid-cols-[1.1fr_1fr]">
      <aside className="relative hidden overflow-hidden bg-navy-900 lg:block">
        <img src="/brand/hero.webp" alt="" className="absolute inset-0 size-full object-cover object-[50%_20%]" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 from-30% via-navy-950/85 via-55% to-transparent to-85%" />
        <div className="relative flex h-full flex-col justify-between p-10 xl:p-14">
          <div className="inline-flex w-fit rounded-2xl bg-white/80 px-4 py-2 shadow-soft backdrop-blur">
            <Wordmark />
          </div>
          <div className="max-w-xl text-white">
            <h1 className="font-heading text-3xl font-semibold leading-tight xl:text-4xl">{BRAND.tagline}</h1>
            <p className="mt-4 text-lg leading-relaxed text-white/85">{BRAND.description}</p>
            <ul className="mt-8 space-y-3">
              {PROMISES.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-center gap-3 text-[0.95rem] text-white/90">
                  <span className="grid size-9 place-items-center rounded-full bg-white/15 ring-1 ring-white/25">
                    <Icon className="size-[18px]" aria-hidden="true" />
                  </span>
                  {text}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </aside>

      <main className="flex min-h-dvh flex-col px-4 pb-[max(1rem,var(--safe-bottom))] pt-[max(1.5rem,var(--safe-top))] sm:px-8">
        <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-6">
          <div className="mb-6 flex flex-col items-center gap-3 text-center lg:hidden">
            <BrandMark className="size-16" />
            <Wordmark className="[&>span:first-child]:hidden" />
            <p className="text-sm text-ink-muted">{BRAND.tagline}</p>
          </div>
          <div className="animate-slide-up rounded-3xl border border-line bg-surface p-6 shadow-lift sm:p-8">{children}</div>
          <p className="mt-6 text-center text-xs leading-relaxed text-ink-muted">
            MedicAiL is not a substitute for a doctor. In an emergency call your local emergency number.
          </p>
        </div>
      </main>
    </div>
  );
}

export function AuthHeading({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return (
    <header className="mb-6">
      <h2 className="font-heading text-2xl font-semibold text-ink">{title}</h2>
      {subtitle && <p className="mt-1.5 text-[0.95rem] leading-relaxed text-ink-muted">{subtitle}</p>}
    </header>
  );
}

/** Polite live region for form level errors and notices. */
export function FormMessage({ error, notice }: { error?: string | null; notice?: string | null }) {
  return (
    <div aria-live="polite" className="empty:hidden">
      {error && (
        <p role="alert" className="mb-4 rounded-xl border border-danger-500/30 bg-danger-50 px-3.5 py-2.5 text-sm text-danger-600 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </p>
      )}
      {!error && notice && (
        <p className="mb-4 rounded-xl border border-leaf-500/30 bg-leaf-50 px-3.5 py-2.5 text-sm text-leaf-600 dark:bg-leaf-500/10 dark:text-green-300">
          {notice}
        </p>
      )}
    </div>
  );
}
